import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Card,
    Form,
    Input,
    InputNumber,
    Button,
    Space,
    Typography,
    message,
} from 'antd';
import { AppInfoRequest } from '../../types/app-info.types';
import { appInfoService } from '../../services/AppInfoService';
import { OperationStatus } from '../../types/api.types';

const { Title } = Typography;

export const AppInfoForm: React.FC = () => {
    const { id: paramId } = useParams<{ id: string }>();
    const location = useLocation();
    const stateId = location.state?.id;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    
    // Use the ID from state if available, otherwise use the ID from URL params
    const id = stateId || paramId;
    const isEdit = Boolean(id);

    useEffect(() => {
        if (isEdit && id) {
            loadAppInfo(id);
        }
    }, [id, form]);

    const loadAppInfo = async (appInfoId: string) => {
        try {
            const response = await appInfoService.getById(appInfoId);
            if (response.status === OperationStatus.SUCCESS && response.data) {
                form.setFieldsValue({...response.data});
            } else {
                message.error(response.message || 'Failed to load app information');
                navigate('/dashboard/app-info/list');
            }
        } catch (error) {
            message.error('Failed to load app information');
            navigate('/dashboard/app-info/list');
        }
    };

    const handleSubmit = async (values: AppInfoRequest) => {
        try {
            const response = isEdit && id
                ? await appInfoService.update(id, values)
                : await appInfoService.create(values);

            if (response.status === OperationStatus.SUCCESS) {
                message.success(`App info ${isEdit ? 'updated' : 'created'} successfully`);
                navigate('/dashboard/app-info/list');
            } else {
                message.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} app info`);
            }
        } catch (error) {
            message.error(`Failed to ${isEdit ? 'update' : 'create'} app info`);
        }
    };

    const handleCreateNew = async () => {
        try {
            const values = await form.validateFields();
            const response = await appInfoService.create(values);

            if (response.status === OperationStatus.SUCCESS) {
                message.success('App info created successfully');
                navigate('/dashboard/app-info/list');
            } else {
                message.error(response.message || 'Failed to create app info');
            }
        } catch (error: any) {
            if (error?.errorFields) {
                // Form validation errors are handled by Ant Design
                return;
            }
            message.error('Failed to create app info');
        }
    };

    return (
        <Card>
            <Title level={4} style={{ marginBottom: 24 }}>
                {isEdit ? 'Edit App Information' : 'Add New App Information'}
            </Title>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ versionCode: 1 }}
                requiredMark
            >
                <Form.Item
                    label="App Name"
                    name="appName"
                    rules={[
                        { required: true, message: 'App name is required' },
                        { pattern: /^[a-zA-Z0-9_]+$/, message: 'App name must contain only letters, numbers and underscores' },
                        { type: 'string', min: 3, message: 'App name must be at least 3 characters' }
                    ]}
                >
                    <Input placeholder="My App" />
                </Form.Item>

                <Form.Item
                    label="Package Name"
                    name="packageName"
                    rules={[
                        { required: true, message: 'Package name is required' },
                        { type: 'string', min: 3, message: 'Package name must be at least 3 characters' }
                    ]}
                >
                    <Input placeholder="com.example.app" />
                </Form.Item>

                <Form.Item
                    label="Version"
                    name="version"
                    rules={[
                        { required: true, message: 'Version is required' }
                    ]}
                >
                    <Input placeholder="1.0" />
                </Form.Item>

                <Form.Item
                    label="Version Code"
                    name="versionCode"
                    rules={[
                        { required: true, message: 'Version code is required' },
                        { type: 'number', min: 1, message: 'Version code must be greater than 0' }
                    ]}
                >
                    <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    label="Download Link"
                    name="downloadLink"
                    rules={[
                        { type: 'url', message: 'Please enter a valid URL' }
                    ]}
                >
                    <Input placeholder="https://example.com/app.apk" />
                </Form.Item>

                <Form.Item
                    label="Admin Component Name"
                    name="adminComponentName"
                >
                    <Input placeholder="com.example.app.admin" />
                </Form.Item>

                <Form.Item
                    label="Client Signature Checksum"
                    name="clientSignatureChecksum"
                >
                    <Input placeholder="SHA-256 checksum" />
                </Form.Item>

                <Form.Item
                    label="Firebase Restricted Package Name"
                    name="firebasePackageName"
                >
                    <Input placeholder="com.example.app.restricted" />
                </Form.Item>

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">
                            {isEdit ? 'Update' : 'Create'}
                        </Button>
                        {isEdit && (
                            <Button type="default" onClick={handleCreateNew}>
                                Create New
                            </Button>
                        )}
                        <Button onClick={() => navigate('/dashboard/app-info/list')}>
                            Cancel
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
}; 